import { Breadcrumb, Button, Input, Modal, Table } from "antd";
import {
  HomeOutlined,
  LogoutOutlined,
  ReloadOutlined,
  SearchOutlined,
  TeamOutlined,
  UserOutlined,
  WifiOutlined,
} from "@ant-design/icons";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import { openNotification } from "../../../components/Notification";
import { UserContext } from "../../../contexts/UserContext";
import { usuariosLogadosService } from "../../../services/resources/usuariosLogadosService";
import { conectarUsuariosLogados } from "../../../services/resources/usuariosLogadosSocket";
import {
  OPCOES_TAMANHO_PAGINA,
  podeConsultarUsuariosLogados,
  podeEncerrarSessoesUsuarios,
  urls,
} from "../../../utils";
import { colunasUsuariosOnline } from "../colunas";
import { ehUsuarioAtual, FILTROS_VAZIOS, montarFiltros } from "../format";
import "../usuariosLogados.css";

const INTERVALO_ATUALIZACAO_MS = 60000;
const INTERVALO_RELATIVO_MS = 15000;
const DEBOUNCE_BUSCA_MS = 400;

export const ListagemUsuariosLogados = () => {
  const { user } = useContext(UserContext);
  const autorizado = podeConsultarUsuariosLogados(user);
  const podeEncerrar = podeEncerrarSessoesUsuarios(user);
  const [loading, setLoading] = useState(false);
  const [dados, setDados] = useState([]);
  const [selecionados, setSelecionados] = useState([]);
  const [encerrando, setEncerrando] = useState(false);
  const [busca, setBusca] = useState("");
  const [perfil, setPerfil] = useState("");
  const [login, setLogin] = useState("");
  const [instituicao, setInstituicao] = useState("");
  const [totalAtivos, setTotalAtivos] = useState(0);
  const [atualizadoEm, setAtualizadoEm] = useState(null);
  const [agora, setAgora] = useState(Date.now());
  const [paginacao, setPaginacao] = useState({
    currentPage: 0,
    totalElements: 0,
    size: OPCOES_TAMANHO_PAGINA[1] || 8,
  });
  const consultaRef = useRef({ ...FILTROS_VAZIOS, page: 0, size: 8 });
  const debounceRef = useRef();

  const carregar = useCallback(async (page, size, filtros, silencioso) => {
    consultaRef.current = {
      ...FILTROS_VAZIOS,
      ...filtros,
      page,
      size,
    };
    if (!silencioso) {
      setLoading(true);
    }
    try {
      const { data } = await usuariosLogadosService.listar({
        page,
        size,
        busca: filtros.busca || "",
        nome: filtros.nome || "",
        cpf: filtros.cpf || "",
        perfil: filtros.perfil || "",
        login: filtros.login || "",
        instituicao: filtros.instituicao || "",
      });
      setDados(data.content || []);
      setPaginacao({
        currentPage: data.currentPage,
        totalElements: data.totalElements,
        size: data.size,
      });
      setTotalAtivos(
        typeof data.totalAtivos === "number" ? data.totalAtivos : data.totalElements || 0
      );
      setAtualizadoEm(new Date());
      setAgora(Date.now());
    } catch (error) {
      if (!silencioso) {
        openNotification(
          "error",
          <strong>Não foi possível listar os usuários logados</strong>,
          error?.response?.data?.userMessage || error?.response?.data?.detail
        );
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!autorizado) return undefined;
    carregar(0, paginacao.size, FILTROS_VAZIOS, false);
    const recarregar = () => {
      const atual = consultaRef.current;
      carregar(atual.page, atual.size, atual, true);
    };
    const desconectar = conectarUsuariosLogados(recarregar);
    const intervalo = setInterval(recarregar, INTERVALO_ATUALIZACAO_MS);
    const relativo = setInterval(() => setAgora(Date.now()), INTERVALO_RELATIVO_MS);
    return () => {
      desconectar();
      clearInterval(intervalo);
      clearInterval(relativo);
      clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autorizado, carregar]);

  // Quem saiu do sistema (ou da página atual da lista) deixa de estar selecionado
  useEffect(() => {
    setSelecionados((anteriores) =>
      anteriores.filter((item) => dados.some(({ codigo }) => codigo === item.codigo))
    );
  }, [dados]);

  const encerrarSessoes = useCallback(
    async (usuarios) => {
      setEncerrando(true);
      try {
        await usuariosLogadosService.encerrarSessoes(usuarios.map(({ codigo }) => codigo));
        openNotification(
          "success",
          <strong>
            {usuarios.length === 1
              ? `Sessão de ${usuarios[0].nome} encerrada`
              : `${usuarios.length} sessões encerradas`}
          </strong>
        );
        setSelecionados([]);
        const atual = consultaRef.current;
        carregar(atual.page, atual.size, atual, true);
      } catch (error) {
        openNotification(
          "error",
          <strong>Não foi possível encerrar as sessões</strong>,
          error?.response?.data?.userMessage || error?.response?.data?.detail
        );
      } finally {
        setEncerrando(false);
      }
    },
    [carregar]
  );

  const confirmarEncerramentoSelecionados = () => {
    Modal.confirm({
      title: `Encerrar ${selecionados.length} ${
        selecionados.length === 1 ? "sessão" : "sessões"
      }?`,
      content: (
        <>
          <p>Os usuários abaixo voltarão para a tela de login em todos os dispositivos:</p>
          <ul className="usuarios-online-encerrar-lista">
            {selecionados.map(({ codigo, nome }) => (
              <li key={codigo}>{nome}</li>
            ))}
          </ul>
        </>
      ),
      okText: "Encerrar sessões",
      okButtonProps: { danger: true },
      cancelText: "Cancelar",
      onOk: () => encerrarSessoes(selecionados),
    });
  };

  const filtros = montarFiltros({ busca, perfil, login, instituicao });
  const outrosUsuarios = Math.max(0, (totalAtivos || 0) - 1);
  const nomeSessao = user?.nome_completo || "Sua sessão";
  const colunas = useMemo(
    () =>
      colunasUsuariosOnline({
        user,
        agora,
        podeEncerrar,
        onEncerrar: encerrarSessoes,
        encerrando,
      }),
    [user, agora, podeEncerrar, encerrarSessoes, encerrando]
  );

  if (!autorizado) {
    return null;
  }

  const definirCampo = (campo, valor) => {
    if (campo === "busca") setBusca(valor);
    if (campo === "perfil") setPerfil(valor);
    if (campo === "login") setLogin(valor);
    if (campo === "instituicao") setInstituicao(valor);
  };

  const aoAlterarFiltro = (campo, valor) => {
    const proximos = { busca, perfil, login, instituicao, [campo]: valor };
    definirCampo(campo, valor);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      carregar(0, paginacao.size, montarFiltros(proximos), false);
    }, DEBOUNCE_BUSCA_MS);
  };

  return (
    <div id="sectionMain" className="usuarios-online-pagina">
      <Breadcrumb id="breadcrumb">
        <Breadcrumb.Item key="home">
          <Link to="">
            <HomeOutlined />
          </Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item key="acessos">controle de acesso</Breadcrumb.Item>
        <Breadcrumb.Item key="logados">
          <Link to={urls.USUARIOS_LOGADOS_LIST}>
            <strong>usuários online</strong>
          </Link>
        </Breadcrumb.Item>
      </Breadcrumb>
      <div className="container-section">
        <section className="usuarios-online">
          <header className="usuarios-online-cabecalho">
            <div className="usuarios-online-titulo">
              <span className="usuarios-online-titulo__icone" aria-hidden>
                <TeamOutlined />
              </span>
              <div>
                <h2>Usuários online</h2>
                <p>Monitoramento em tempo real das sessões ativas no sistema</p>
              </div>
            </div>
            <div className="usuarios-online-acoes">
              {atualizadoEm ? (
                <span className="usuarios-online-hora">
                  Atualizado {moment(atualizadoEm).format("HH:mm:ss")}
                </span>
              ) : null}
              <Button
                type="primary"
                icon={<ReloadOutlined />}
                onClick={() => carregar(paginacao.currentPage, paginacao.size, filtros, false)}
              >
                Atualizar
              </Button>
            </div>
          </header>

          <div className="usuarios-online-kpis">
            <article className="usuarios-online-kpi usuarios-online-kpi--destaque">
              <span>CONECTADOS AGORA</span>
              <strong>{totalAtivos || 0}</strong>
              <small>Inclui a sua sessão</small>
            </article>
            <article className="usuarios-online-kpi">
              <span>OUTROS USUÁRIOS</span>
              <strong>{outrosUsuarios}</strong>
              <small>Além de você</small>
            </article>
            <article className="usuarios-online-kpi">
              <span>SUA SESSÃO</span>
              <strong>
                <UserOutlined /> Ativa
              </strong>
              <small>{nomeSessao}</small>
            </article>
            <article className="usuarios-online-kpi">
              <span>ATUALIZAÇÃO</span>
              <strong>
                <WifiOutlined /> Ao vivo
              </strong>
              <small>WebSocket em tempo real</small>
            </article>
          </div>

          <div className="usuarios-online-lista">
            <div className="usuarios-online-lista__topo">
              <div className="usuarios-online-lista__titulo">
                <h3>Pessoas conectadas</h3>
                <span className="usuarios-online-vivo">
                  <i /> AO VIVO
                </span>
              </div>
              {podeEncerrar ? (
                <Button
                  danger
                  icon={<LogoutOutlined />}
                  disabled={!selecionados.length}
                  loading={encerrando}
                  onClick={confirmarEncerramentoSelecionados}
                >
                  {selecionados.length
                    ? `Encerrar selecionadas (${selecionados.length})`
                    : "Encerrar selecionadas"}
                </Button>
              ) : null}
            </div>
            <div className="usuarios-online-filtros">
              <Input
                allowClear
                prefix={<SearchOutlined />}
                placeholder="Nome ou CPF"
                value={busca}
                onChange={(evento) => aoAlterarFiltro("busca", evento?.target?.value || "")}
                onPressEnter={() => {
                  clearTimeout(debounceRef.current);
                  carregar(0, paginacao.size, filtros, false);
                }}
              />
              <Input
                allowClear
                placeholder="Perfil"
                value={perfil}
                onChange={(evento) => aoAlterarFiltro("perfil", evento?.target?.value || "")}
                onPressEnter={() => {
                  clearTimeout(debounceRef.current);
                  carregar(0, paginacao.size, filtros, false);
                }}
              />
              <Input
                allowClear
                placeholder="Login"
                value={login}
                onChange={(evento) => aoAlterarFiltro("login", evento?.target?.value || "")}
                onPressEnter={() => {
                  clearTimeout(debounceRef.current);
                  carregar(0, paginacao.size, filtros, false);
                }}
              />
              <Input
                allowClear
                placeholder="Instituição"
                value={instituicao}
                onChange={(evento) => aoAlterarFiltro("instituicao", evento?.target?.value || "")}
                onPressEnter={() => {
                  clearTimeout(debounceRef.current);
                  carregar(0, paginacao.size, filtros, false);
                }}
              />
            </div>
            <Table
              rowKey="codigo"
              loading={loading}
              columns={colunas}
              dataSource={dados}
              rowSelection={
                podeEncerrar
                  ? {
                      selectedRowKeys: selecionados.map(({ codigo }) => codigo),
                      onChange: (_chaves, linhas) => setSelecionados(linhas),
                      getCheckboxProps: (item) => ({
                        disabled: ehUsuarioAtual(item, user),
                        title: ehUsuarioAtual(item, user)
                          ? "Use a opção sair para encerrar a sua própria sessão"
                          : undefined,
                      }),
                    }
                  : undefined
              }
              rowClassName={(item) =>
                ehUsuarioAtual(item, user) ? "usuarios-online-linha--voce" : ""
              }
              pagination={{
                current: paginacao.currentPage + 1,
                pageSize: paginacao.size,
                total: paginacao.totalElements,
                showSizeChanger: true,
                pageSizeOptions: OPCOES_TAMANHO_PAGINA,
                hideOnSinglePage: paginacao.totalElements <= paginacao.size,
              }}
              onChange={(pager, _tableFilters, _sorter, extra) => {
                if (extra?.action === "sort") {
                  return;
                }
                carregar((pager.current || 1) - 1, pager.pageSize, filtros, false);
              }}
              locale={{ emptyText: "Nenhuma pessoa conectada" }}
            />
          </div>
        </section>
      </div>
    </div>
  );
};
